import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Calendar, Check, ChevronRight, Search, X } from "lucide-react";
// The shared shadcn day-grid calendar (react-day-picker), aliased so it doesn't
// collide with lucide's Calendar icon.
import { Calendar as DayCalendar } from "@/components/ui/calendar";
import { RANGE_MAX_ISO, RANGE_MIN_ISO, formatDateLong, formatDateRangeLong, fromIso, isoLocal } from "@/lib/utils/daylight-insight/dates";
import { toDisplayLabel } from "@/lib/utils/daylight-insight/format";
import type { DateRange } from "@/lib/utils/daylight-insight/types";

/** Search box for a dropdown popup — sticky at the top of the option list so it
 *  stays reachable while the list scrolls. Shared by {@link MultiSelect} and
 *  BranchScope, which owns its own popup but must search the same way. */
export function OptionSearch({ query, setQuery, placeholder }: { query: string; setQuery: (v: string) => void; placeholder: string }) {
  return (
    <div className="dl-search">
      <div className="dl-search-in">
        <Search size={13} />
        <input
          autoFocus
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // Enter/Escape inside a filter box shouldn't reach the page behind it.
          onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setQuery(""); } }}
        />
        {query && <button onClick={() => setQuery("")} aria-label="Clear search"><X size={12} /></button>}
      </div>
    </div>
  );
}

interface MultiSelectProps {
  label: string;
  icon: ReactNode;
  options: string[];
  selected: string[];
  onChange: (v: string[]) => void;
  allLabel: string;
}
export function MultiSelect({ label, icon, options, selected, onChange, allLabel }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  // A closed dropdown drops its search, so reopening always starts from the full
  // list rather than a filter the user has long forgotten setting.
  useEffect(() => { if (!open) setQuery(""); }, [open]);
  // Options are re-cased for DISPLAY only. `selected`/`onChange` keep the raw
  // value, because several filters send the selected string to the SP verbatim.
  const txt = selected.length === 0 ? allLabel : selected.length === options.length ? allLabel : selected.length === 1 ? toDisplayLabel(selected[0]) : `${selected.length} selected`;
  const toggle = (o: string) => onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  // Matches against what's on screen AND the raw value, so typing either finds it.
  const shown = searching
    ? options.filter((o) => o.toLowerCase().includes(q) || toDisplayLabel(o).toLowerCase().includes(q))
    : options;
  // While searching, "Select all" means "everything I can currently see" — added
  // to the existing selection, so narrowing then selecting builds a list up.
  const selectAll = () => onChange(searching ? Array.from(new Set([...selected, ...shown])) : [...options]);
  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="dl-ctrl" onClick={() => setOpen(!open)}>
        {icon}{label}: <span style={{ color: "var(--ink-2)" }}>{txt}</span>
        <ChevronRight size={13} style={{ transform: open ? "rotate(90deg)" : "rotate(90deg)", color: "var(--ink-3)" }} />
      </button>
      {open && (
        <div className="dl-pop">
          <OptionSearch query={query} setQuery={setQuery} placeholder={`Search ${label.toLowerCase()}…`} />
          <div style={{ display: "flex", gap: 10, padding: "6px 10px 8px", borderBottom: "1px solid var(--line-soft)", marginBottom: 4 }}>
            <button onClick={selectAll} style={{ fontSize: 11, color: "var(--hermes)", fontWeight: 600, background: "none", border: 0, cursor: "pointer", padding: 0 }}>Select all</button>
            <button onClick={() => onChange([])} style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, background: "none", border: 0, cursor: "pointer", padding: 0 }}>Clear</button>
          </div>
          {/* The "All …" row is a reset action, not a searchable option — it hides
              while a search is narrowing the list. */}
          {!searching && (
            <div className="dl-opt" onClick={() => onChange([])}>
              <span className="dl-box" style={selected.length === 0 ? { background: "var(--hermes)", borderColor: "var(--hermes)", color: "#fff" } : {}}>{selected.length === 0 && <Check size={11} />}</span>{allLabel}
            </div>
          )}
          {shown.map((o) => (
            <div key={o} className="dl-opt" onClick={() => toggle(o)}>
              <span className={"dl-box" + (selected.includes(o) ? " on" : "")}>{selected.includes(o) && <Check size={11} />}</span>{toDisplayLabel(o)}
            </div>
          ))}
          {shown.length === 0 && <div className="dl-noopt">No matches for “{query.trim()}”.</div>}
        </div>
      )}
    </div>
  );
}

/** One dimension the Focus control offers — a dashboard's non-primary filter. */
export interface FocusDimension {
  key: string;
  label: string;
  options: string[];
  selected: string[];
}

/**
 * "+ Focus" — the filter bar's entry point for the dashboard's NON-primary
 * filters. Pick a dimension, then tick its members (multi-select, like the
 * dropdowns); each selected member becomes a chip beside the button, whose ×
 * drops it and whose label reopens the picker on that dimension. It holds no
 * selection of its own: `dims[].selected` and `onChange` are the page's
 * filter state, the same the Branch / Payor dropdowns write.
 */
export function FocusFilter({ dims, onChange }: { dims: FocusDimension[]; onChange: (key: string, next: string[]) => void }) {
  const [open, setOpen] = useState(false);
  // The dimension whose members are listed; null = the dimension list.
  const [dimKey, setDimKey] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { setQuery(""); }, [open, dimKey]);
  const dim = dims.find((d) => d.key === dimKey) ?? null;
  const openAt = (key: string | null) => { setDimKey(key); setOpen(true); };
  const q = query.trim().toLowerCase();
  const shown = dim
    ? q ? dim.options.filter((o) => o.toLowerCase().includes(q) || toDisplayLabel(o).toLowerCase().includes(q)) : dim.options
    : [];
  const toggle = (o: string) => {
    if (!dim) return;
    onChange(dim.key, dim.selected.includes(o) ? dim.selected.filter((x) => x !== o) : [...dim.selected, o]);
  };
  return (
    <div className="pp-focus" ref={ref}>
      <div style={{ position: "relative" }}>
        <button type="button" className={"pp-focus-add" + (open ? " on" : "")} onClick={() => (open ? setOpen(false) : openAt(null))}>
          + Filters
        </button>
        {open && (
          <div className="dl-pop pp-focus-pop">
            {!dim ? (
              <>
                <div className="pp-focus-ph">Filters · pick a dimension</div>
                {dims.map((d) => (
                  <div key={d.key} className="dl-opt pp-focus-dim" onClick={() => setDimKey(d.key)}>
                    <span>{d.label}</span>
                    {d.selected.length > 0 && <span className="pp-focus-n">{d.selected.length}</span>}
                    <ChevronRight size={13} style={{ color: "var(--ink-3)", marginLeft: "auto" }} />
                  </div>
                ))}
              </>
            ) : (
              <>
                <button type="button" className="pp-focus-back" onClick={() => setDimKey(null)}>
                  <ChevronRight size={13} style={{ transform: "rotate(180deg)" }} />
                  {dim.label} · pick members
                </button>
                <OptionSearch query={query} setQuery={setQuery} placeholder={`Search ${dim.label.toLowerCase()}…`} />
                <div style={{ display: "flex", gap: 10, padding: "6px 10px 8px", borderBottom: "1px solid var(--line-soft)", marginBottom: 4 }}>
                  <button onClick={() => onChange(dim.key, Array.from(new Set([...dim.selected, ...shown])))} style={{ fontSize: 11, color: "var(--hermes)", fontWeight: 600, background: "none", border: 0, cursor: "pointer", padding: 0 }}>Select all</button>
                  <button onClick={() => onChange(dim.key, [])} style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, background: "none", border: 0, cursor: "pointer", padding: 0 }}>Clear</button>
                </div>
                {shown.map((o) => (
                  <div key={o} className="dl-opt" onClick={() => toggle(o)}>
                    <span className={"dl-box" + (dim.selected.includes(o) ? " on" : "")}>{dim.selected.includes(o) && <Check size={11} />}</span>{toDisplayLabel(o)}
                  </div>
                ))}
                {shown.length === 0 && (
                  <div className="dl-noopt">{q ? <>No matches for “{query.trim()}”.</> : "No options."}</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {dims.flatMap((d) => {
        // Every member ticked → one "<Label>: All" chip rather than one per member.
        const all = d.options.length > 0 && d.options.every((o) => d.selected.includes(o));
        if (all) {
          return [
            <span key={`${d.key}\u0000*`} className="pp-fchip">
              <button type="button" className="pp-fchip-l" onClick={() => openAt(d.key)} title={`Edit ${d.label} focus`}>
                {d.label}: All
              </button>
              <button
                type="button"
                className="pp-fchip-x"
                onClick={() => onChange(d.key, [])}
                aria-label={`Remove ${d.label}: All`}
                title="Remove"
              >
                <X size={11} />
              </button>
            </span>,
          ];
        }
        return d.selected.map((o) => (
          <span key={`${d.key}\u0000${o}`} className="pp-fchip">
            <button type="button" className="pp-fchip-l" onClick={() => openAt(d.key)} title={`Edit ${d.label} focus`}>
              {d.label}: {toDisplayLabel(o)}
            </button>
            <button
              type="button"
              className="pp-fchip-x"
              onClick={() => onChange(d.key, d.selected.filter((x) => x !== o))}
              aria-label={`Remove ${d.label}: ${o}`}
              title="Remove"
            >
              <X size={11} />
            </button>
          </span>
        ));
      })}
    </div>
  );
}

interface DateRangePickerProps {
  range: DateRange;
  onChange: (r: DateRange) => void;
  /** Latest selectable day (YYYY-MM-DD). Callers pass the store's
   *  `maxDateIso` — the warehouse refresh date — so no day the warehouse has
   *  no data for can be picked. Defaults to today. */
  maxIso?: string;
}
/**
 * The shadcn Calendar paints its selected day from the GLOBAL `--primary`
 * token, which is Skyra blue. The daylight module is Hermes orange, and its
 * `--hermes` variable is a plain hex the Tailwind `hsl(var(--x))` classes can't
 * consume — so the token itself is re-pointed on the calendar wrapper. Scoped
 * to this subtree, so the rest of the app keeps its blue primary.
 */
const CAL_ACCENT_VARS = {
  "--primary": "25 93% 48%", // #EC6608
  "--primary-foreground": "0 0% 100%",
  "--ring": "25 93% 48%",
} as CSSProperties;

/** One labelled date row of the range popup: the field button showing the long
 *  "Aug 01, 2026" form, and — while it's the open one — the month calendar
 *  floating beneath it, over whatever the panel holds below. */
function DateField({
  label, value, open, onToggle, onSelect, disabled,
}: {
  label: string;
  value: string;
  open: boolean;
  onToggle: () => void;
  onSelect: (iso: string) => void;
  /** Days the calendar must refuse (out of range, or crossing the other end). */
  disabled: (d: Date) => boolean;
}) {
  const selected = fromIso(value);
  return (
    <div className="dl-dategroup">
      <div className="dl-datelbl">{label}</div>
      <button type="button" className={"dl-datefield" + (open ? " on" : "")} onClick={onToggle}>
        <Calendar size={15} style={{ color: "var(--ink-3)", flexShrink: 0 }} />
        <span>{formatDateLong(value)}</span>
      </button>
      {open && (
        <div className="dl-datecal" style={CAL_ACCENT_VARS}>
          <DayCalendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            disabled={disabled}
            onSelect={(d) => d && onSelect(isoLocal(d))}
            initialFocus
          />
        </div>
      )}
    </div>
  );
}

export function DateRangePicker({ range, onChange, maxIso = RANGE_MAX_ISO }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<DateRange>(range);
  // Which field's calendar is expanded — only ever one, as in the design.
  const [field, setField] = useState<"from" | "to" | null>(null);
  useEffect(() => setDraft(range), [range]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  // Closing without applying throws the edits away — reopening always starts
  // from the range actually on screen.
  const close = () => { setDraft(range); setField(null); setOpen(false); };

  const minDate = fromIso(RANGE_MIN_ISO);
  const maxDate = fromIso(maxIso);
  // The only bound either calendar carries is the reportable window — neither
  // field is narrowed by the other, so any day can be picked in either one.
  const outOfRange = (d: Date) => d < minDate || d > maxDate;

  // …which means the two can end up the wrong way round. Read them as the range
  // the user described rather than refusing it: the earlier day is the start.
  const apply = () => {
    const { start, end } = draft;
    onChange(start <= end ? draft : { start: end, end: start });
    setField(null);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="dl-ctrl" onClick={() => (open ? close() : setOpen(true))}>
        <Calendar size={14} style={{ color: "var(--ink-3)" }} />{formatDateRangeLong(range)}
      </button>
      {open && (
        <div className="dl-pop dl-datepop">
          <DateField
            label="From Date"
            value={draft.start}
            open={field === "from"}
            onToggle={() => setField(field === "from" ? null : "from")}
            disabled={outOfRange}
            onSelect={(v) => { setDraft({ ...draft, start: v }); setField(null); }}
          />
          <DateField
            label="To Date"
            value={draft.end}
            open={field === "to"}
            onToggle={() => setField(field === "to" ? null : "to")}
            disabled={outOfRange}
            onSelect={(v) => { setDraft({ ...draft, end: v }); setField(null); }}
          />
          <div className="dl-datefoot">
            <button className="dl-btn" onClick={close}>Cancel</button>
            <button className="dl-btn brand" onClick={apply}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}

interface DateSinglePickerProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  /** Latest selectable day (YYYY-MM-DD) — see {@link DateRangePickerProps}. */
  maxIso?: string;
}
/** Single-date picker — the same panel as {@link DateRangePicker} (labelled
 *  field, overlaid month calendar, Cancel/Apply footer) with ONE date rather
 *  than a from/to pair. Used by All Metrics' By Month/Week/Day "as of" date. */
export function DateSinglePicker({ value, onChange, label = "As of", maxIso = RANGE_MAX_ISO }: DateSinglePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState(value);
  const [calOpen, setCalOpen] = useState(false);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const close = () => { setDraft(value); setCalOpen(false); setOpen(false); };

  const minDate = fromIso(RANGE_MIN_ISO);
  const maxDate = fromIso(maxIso);

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="dl-ctrl" onClick={() => (open ? close() : setOpen(true))}>
        <Calendar size={14} style={{ color: "var(--ink-3)" }} />{label}: <span style={{ color: "var(--ink-1)", fontWeight: 600 }}>{formatDateLong(value)}</span>
      </button>
      {open && (
        <div className="dl-pop dl-datepop">
          <DateField
            label="Date"
            value={draft}
            open={calOpen}
            onToggle={() => setCalOpen(!calOpen)}
            disabled={(d) => d < minDate || d > maxDate}
            onSelect={(v) => { setDraft(v); setCalOpen(false); }}
          />
          <div className="dl-datefoot">
            <button className="dl-btn" onClick={close}>Cancel</button>
            <button className="dl-btn brand" onClick={() => { if (draft) { onChange(draft); setCalOpen(false); setOpen(false); } }}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}

interface DateAnchorProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  /** Latest selectable day (YYYY-MM-DD) — see {@link DateRangePickerProps}. */
  maxIso?: string;
}
export function DateAnchor({ value, onChange, label = "As of", maxIso = RANGE_MAX_ISO }: DateAnchorProps) {
  const d = new Date(value + "T00:00:00");
  const pretty = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return (
    <label className="dl-ctrl" style={{ cursor: "pointer", position: "relative" }}>
      <Calendar size={14} style={{ color: "var(--ink-3)" }} />{label}: <span style={{ color: "var(--ink-1)", fontWeight: 600 }}>{pretty}</span>
      <input type="date" value={value} min={RANGE_MIN_ISO} max={maxIso} onChange={(e) => e.target.value && onChange(e.target.value)}
        style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%" }} />
    </label>
  );
}

interface RangeSliderProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
  fmt: (v: number) => string;
}
export function RangeSlider({ label, min, max, step, value, onChange, fmt }: RangeSliderProps) {
  const [lo, hi] = value;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  return (
    <div className="slider">
      <div className="slider-lbl"><span>{label}</span><b>{fmt(lo)}–{fmt(hi)}</b></div>
      <div className="slider-track">
        <div className="rail" />
        <div className="fill" style={{ left: pct(lo) + "%", right: (100 - pct(hi)) + "%" }} />
        <input type="range" min={min} max={max} step={step} value={lo} onChange={(e) => onChange([Math.min(+e.target.value, hi - step), hi])} />
        <input type="range" min={min} max={max} step={step} value={hi} onChange={(e) => onChange([lo, Math.max(+e.target.value, lo + step)])} />
      </div>
    </div>
  );
}
